import { CardEnum, MAX_KNIGHT_LEVEL, TileCornerBuildingType } from '@colonist/game-states';
import { PieceTypes } from '@colonist/game-base-types';
import { getCardsForPiece } from '@colonist/game-card-types';
export class MechanicValidatorKnightPlayer {
    getKnightPlayerState() {
        return this.knightPlayerState;
    }
    getKnightStates() {
        const knightStates = Object.values(this.knightPlayerState.knights);
        return knightStates.filter((knight)=>knight !== undefined);
    }
    getKnightAtLocation(cornerIndex) {
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex !== cornerIndex) continue;
            return knight;
        }
        return undefined;
    }
    getPlayerKnightStrength() {
        const knightStrengths = this.getKnightStates().map((knight)=>MechanicValidatorKnightPlayer.getKnightStrength(knight));
        return knightStrengths.reduce((x, acc)=>x + acc);
    }
    getKnightWithLevelFromInventory(level, ignoreIsLevelUnlocked = false) {
        for (const knight of this.getKnightStates()){
            const isKnightInInventory = knight.locationCornerIndex === undefined;
            if (!isKnightInInventory) continue;
            if (knight.level !== level) continue;
            const isKnightLevelValid = ignoreIsLevelUnlocked || knight.level <= this.knightPlayerState.maxPurchaseableLevelUnlocked;
            if (!isKnightLevelValid) continue;
            return knight;
        }
        return undefined;
    }
    getKnightWithHighestLevelFromInventory(maximumLevel, ignoreUnlockRestriction) {
        for(let currentLevel = maximumLevel; currentLevel > 0; currentLevel--){
            const knightWithLevel = this.getKnightWithLevelFromInventory(currentLevel, ignoreUnlockRestriction);
            if (knightWithLevel === undefined) continue;
            return knightWithLevel;
        }
        return undefined;
    }
    getKnightsWithLevelFromMap(level) {
        const allKnightsWithLevel = [];
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex === undefined) continue;
            if (knight.level !== level) continue;
            allKnightsWithLevel.push(knight);
        }
        return allKnightsWithLevel;
    }
    getAmountOfKnightsEligibleForActivation() {
        let knightsEligibleForActivation = 0;
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex === undefined) continue;
            if (knight.isActivated === true) continue;
            if (knight.isActivatedThisTurn) continue;
            knightsEligibleForActivation += 1;
        }
        return knightsEligibleForActivation;
    }
    getAmountOfKnightsEligibleForUpgrade() {
        let knightsEligibleForUpgrade = 0;
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex === undefined) continue;
            if (knight.isUpgradedThisTurn) continue;
            const higherLevelKnightAvailable = this.getKnightWithLevelFromInventory(knight.level + 1);
            if (higherLevelKnightAvailable == undefined) continue;
            knightsEligibleForUpgrade += 1;
        }
        return knightsEligibleForUpgrade;
    }
    getAmountOfKnightsOfLevelThatCanBePlacedOnMap(level) {
        let amountOfKnightsOfLevelThatCanBePlacedOnMap = 0;
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex !== undefined) continue;
            if (knight.level !== level) continue;
            amountOfKnightsOfLevelThatCanBePlacedOnMap += 1;
        }
        return amountOfKnightsOfLevelThatCanBePlacedOnMap;
    }
    canPlayerPlaceNewKnight(useResources) {
        const where = this.whereCanPlayerPlaceNewKnight();
        const doesPlayerHaveResourcesAndPiecesForNewKnight = this.hasResourcesAndPiecesForNewKnight(useResources);
        doesPlayerHaveResourcesAndPiecesForNewKnight.validHoverLocations = where;
        if (!doesPlayerHaveResourcesAndPiecesForNewKnight.canTakeAction) return doesPlayerHaveResourcesAndPiecesForNewKnight;
        if (where.length <= 0) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noWhereToPlaceKnight'
            },
            validHoverLocations: where
        };
        return {
            canTakeAction: true,
            validHoverLocations: where
        };
    }
    whereCanPlayerPlaceNewKnight() {
        const placesToBuildNewKnights = [];
        for (const tileEdge of this.mapValidator.tileState.tileEdges){
            if (tileEdge.type == undefined) continue;
            if (tileEdge.owner != this.playerColor) continue;
            for (const touchingCorners of tileEdge.hexEdge.endPoints()){
                const touchingTileCorner = this.mapValidator.tileState.getTileCorner(touchingCorners);
                if (touchingTileCorner == undefined) continue;
                if (touchingTileCorner.buildingType != undefined) continue;
                if (touchingTileCorner.owner != undefined) continue;
                if (!this.mapValidator.tileState.isCornerTouchingLand(touchingTileCorner)) continue;
                const cornerIndex = this.mapValidator.tileState.getTileCornerIndex(touchingTileCorner.hexCorner);
                if (cornerIndex == undefined) continue;
                placesToBuildNewKnights.push(cornerIndex);
            }
        }
        return placesToBuildNewKnights;
    }
    hasResourcesAndPiecesForNewKnight(useResources) {
        const unusedLevel1Knight = this.getKnightWithLevelFromInventory(1);
        if (unusedLevel1Knight == undefined) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughLevelOneKnights'
            }
        };
        if (!this.hasResourcesForKnight(useResources).canTakeAction) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    hasResourcesForKnight(useResources) {
        if (!useResources) return {
            canTakeAction: true
        };
        const resourcesForKnight = getCardsForPiece(PieceTypes.Knight);
        const hasResourcesForKnight = this.playerValidator.hasResourceCardsInHand(resourcesForKnight);
        if (!hasResourcesForKnight) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    canPlayerUpgradeKnight() {
        const useResources = true;
        const where = this.whereCanPlayerUpgradeKnight();
        const doesPlayerHaveResourcesForKnightUpgrade = this.hasResourcesForKnightUpgrade(useResources);
        doesPlayerHaveResourcesForKnightUpgrade.validHoverLocations = where;
        if (!doesPlayerHaveResourcesForKnightUpgrade.canTakeAction) return doesPlayerHaveResourcesForKnightUpgrade;
        if (where.length <= 0) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noKnightsToUpgrade'
            },
            validHoverLocations: where
        };
        return {
            canTakeAction: true,
            validHoverLocations: where
        };
    }
    whereCanPlayerUpgradeKnight() {
        const whereCanPlayerUpgradeKnight = [];
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex == undefined) continue;
            if (knight.isUpgradedThisTurn) continue;
            const knightWithHigherLevel = this.getKnightWithLevelFromInventory(knight.level + 1);
            const doesPlayerHaveAccessToKnightOfHigherLevel = knightWithHigherLevel !== undefined;
            if (!doesPlayerHaveAccessToKnightOfHigherLevel) continue;
            whereCanPlayerUpgradeKnight.push(knight.locationCornerIndex);
        }
        return whereCanPlayerUpgradeKnight;
    }
    whereUpgradeKnightHasNoHigherLevelPieceInSupply() {
        const blockedLocations = [];
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex == undefined) continue;
            if (knight.isUpgradedThisTurn) continue;
            if (knight.level >= MAX_KNIGHT_LEVEL) continue;
            const higherLevelPiecesAvailable = this.getAmountOfKnightsOfLevelThatCanBePlacedOnMap(knight.level + 1);
            if (higherLevelPiecesAvailable > 0) continue;
            blockedLocations.push(knight.locationCornerIndex);
        }
        return blockedLocations;
    }
    hasResourcesForKnightUpgrade(useResources) {
        const costToPurchaseKnight = useResources ? getCardsForPiece(PieceTypes.Knight) : [];
        const doesPlayerHaveResourcesToUpgradeKnight = this.playerValidator.hasResourceCardsInHand(costToPurchaseKnight);
        if (!doesPlayerHaveResourcesToUpgradeKnight) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    canPlayerActivateKnight() {
        const where = this.whereCanPlayerActivateKnight();
        const doesPlayerHaveResourcesForKnightActivation = this.hasResourcesForKnightActivation();
        doesPlayerHaveResourcesForKnightActivation.validHoverLocations = where;
        if (!doesPlayerHaveResourcesForKnightActivation.canTakeAction) return doesPlayerHaveResourcesForKnightActivation;
        if (where.length <= 0) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noKnightsToActivate'
            },
            validHoverLocations: where
        };
        return {
            canTakeAction: true,
            validHoverLocations: where
        };
    }
    whereCanPlayerActivateKnight() {
        const whereCanPlayerActivateKnight = [];
        for (const knight of this.getKnightStates()){
            if (knight.locationCornerIndex == undefined) continue;
            if (knight.isActivated === true) continue;
            if (knight.isActivatedThisTurn === true) continue;
            whereCanPlayerActivateKnight.push(knight.locationCornerIndex);
        }
        return whereCanPlayerActivateKnight;
    }
    hasResourcesForKnightActivation() {
        const costToActivateKnight = [
            CardEnum.Grain
        ];
        const doesPlayerHaveResources = this.playerValidator.hasResourceCardsInHand(costToActivateKnight);
        if (!doesPlayerHaveResources) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    static getKnightStrength(knight) {
        if (knight.locationCornerIndex === undefined) return 0;
        if (knight.isActivated === false) return 0;
        return knight.level;
    }
    static getKnightCornerBuildingType(knight) {
        switch(knight.isActivated + '|' + knight.level){
            case true + '|' + 1:
                return TileCornerBuildingType.KnightActiveLevel1;
            case true + '|' + 2:
                return TileCornerBuildingType.KnightActiveLevel2;
            case true + '|' + 3:
                return TileCornerBuildingType.KnightActiveLevel3;
            case false + '|' + 1:
                return TileCornerBuildingType.KnightInactiveLevel1;
            case false + '|' + 2:
                return TileCornerBuildingType.KnightInactiveLevel2;
            case false + '|' + 3:
                return TileCornerBuildingType.KnightInactiveLevel3;
        }
        return undefined;
    }
    constructor(playerValidator, mapValidator, knightPlayerState){
        this.playerValidator = playerValidator;
        this.mapValidator = mapValidator;
        this.knightPlayerState = knightPlayerState;
        this.playerColor = this.playerValidator.color;
    }
}
